import { preferencesApi, type PreferencesData } from '../api/preferences';

/**
 * Bridge between the per-key sessionStorage state the pages use and the
 * per-user preferences document stored by the API.
 *
 * - hydrate(): after login, the server document is copied into sessionStorage
 *   so every page finds its filters exactly where it always looked.
 * - save(): every write to a persisted key is debounced and PATCHed.
 * - clear(): on logout, the persisted keys leave sessionStorage.
 */
export const PERSISTED_KEYS = [
  'staffing-filters',
  'weekly-staffing-filters',
  'consultants-filters',
  'projects-filters',
  'whats-new-seen',
  'new-seen',
  // The guided tour of the Saisie page: seen (closed, finished or skipped), and used (the button stops glowing)
  'saisie-tour-seen',
  'saisie-tour-used',
  // The « Taux de staffing » panel of the Saisie, folded into a pill or not
  'saisie-rates-collapsed',
] as const;

const pending: PreferencesData = {};
let timer: ReturnType<typeof setTimeout> | null = null;
let enabled = false;

function flush() {
  timer = null;
  const batch = { ...pending };
  for (const key of Object.keys(pending)) delete pending[key];
  if (!enabled || Object.keys(batch).length === 0) return;
  preferencesApi.patch(batch).catch(() => {
    /* best effort: sessionStorage already has the value */
  });
}

export const preferencesStore = {
  isPersisted(key: string): boolean {
    return (PERSISTED_KEYS as readonly string[]).includes(key);
  },

  hydrate(data: PreferencesData) {
    for (const key of PERSISTED_KEYS) {
      if (key in data) {
        try {
          sessionStorage.setItem(key, JSON.stringify(data[key]));
        } catch {
          /* ignore */
        }
      }
    }
    enabled = true;
  },

  save(key: string, value: unknown) {
    if (!this.isPersisted(key) || !enabled) return;
    pending[key] = value;
    if (timer) clearTimeout(timer);
    timer = setTimeout(flush, 600);
  },

  get<T>(key: string): T | null {
    try {
      const raw = sessionStorage.getItem(key);
      return raw === null ? null : (JSON.parse(raw) as T);
    } catch {
      return null;
    }
  },

  clear() {
    enabled = false;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    for (const key of PERSISTED_KEYS) sessionStorage.removeItem(key);
  },
};
